import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { SG_FETCH_EVENT } from 'constants/actions';

function fmtDate(d) {
  try {
    return d ? new Date(d).toLocaleString() : '-';
  } catch (e) {
    return d || '-';
  }
}

function Badge({ children, color = '#0b5fff' }) {
  return (
    <span style={{
      display: 'inline-block',
      background: color,
      color: '#fff',
      padding: '4px 8px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
      marginLeft: 8
    }}>{children}</span>
  );
}

export default function EventsDetails(props) {
  // support route usage without explicit id prop
  const params = useParams();
  const id = props.id || params.id;

  const dispatch = useDispatch();
  const details = useSelector((s) => s.action_events?.[id] || null);

  useEffect(() => {
    if (id && !details) {
      dispatch({ type: SG_FETCH_EVENT, payload: { id } });
    }
  }, [dispatch, id, details]);

  if (!details) return <div style={{ padding: 12 }}>No details available</div>;

  function KeyValue({ label, value }) {
    return (
      <div style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid #f3f3f3' }}>
        <div style={{ minWidth: 160, color: '#444', fontWeight: 600 }}>{label}</div>
        <div style={{ color: '#222', flex: 1 }}>{value ?? '-'}</div>
      </div>
    );
  }

  function Section({ title, children }) {
    return (
      <section style={{ padding: 18, border: '1px solid #eee', borderRadius: 8, background: '#fff', marginBottom: 12 }}>
        <h4 style={{ margin: '0 0 8px 0' }}>{title}</h4>
        <div>{children}</div>
      </section>
    );
  }

  function PrettyJSON({ obj }) {
    const text = JSON.stringify(obj || {}, null, 2);

    function copy() {
      try {
        if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(text);
      } catch (e) {
        // ignore in non-browser test envs
      }
    }

    return (
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', right: 8, top: 8 }}>
          <button onClick={copy} style={{ border: 'none', background: '#0b5fff', color: '#fff', padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Copy</button>
        </div>
        <pre aria-label="json" style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12, borderRadius: 6, maxHeight: 320, overflow: 'auto', marginTop: 24 }}>{text}</pre>
      </div>
    );
  }

  const responseRate = details.response_rate ?? details.response_rate_percentage ?? 0;

  const missingReminders = !Array.isArray(details.reminder_dates) || details.reminder_dates.length === 0;
  const missingEmailTemplates = (!details.email_subject_template || !details.email_subject_template.toString().trim()) && (!details.email_content_template || !details.email_content_template.toString().trim());

  return (
    <div style={{ maxWidth: 980, padding: 14 }}>
      {/* header */}
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 22 }}>{details.name || details.title}
            <small style={{ display: 'inline-block', marginLeft: 12, color: '#666', fontWeight: 500 }}>{details.id}</small>
          </h2>
          <div style={{ marginTop: 8, color: '#555', lineHeight: '1.5' }}>{details.description || <em>No description provided</em>}</div>
        </div>

        <div style={{ textAlign: 'right', minWidth: 180 }}>
          <div style={{ marginBottom: 8 }}><strong style={{ color: '#333' }}>Type</strong><div style={{ color: '#555' }}>{details.event_type || '-'}</div></div>
          <div style={{ marginBottom: 8 }}><strong style={{ color: '#333' }}>Sequence</strong><div style={{ color: '#555' }}>{details.sequence_number ?? '-'}</div></div>
          <div style={{ marginTop: 8 }}>
            <strong style={{ color: '#333' }}>Status</strong>
            <div style={{ marginTop: 6 }}><Badge color={details.status === 'active' ? '#21a179' : '#888'}>{details.status || 'unknown'}</Badge></div>
          </div>
        </div>
      </div>

      {/* Developer Notes */}
      <Section title="🔍 Developer Notes — Event Status Checks">
        <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 6, padding: 12 }}>
          <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 8 }}>Why This Event May Not Be Progressing</div>
          <div style={{ color: '#78350f', fontSize: 13, lineHeight: '1.6' }}>
            <div style={{ marginBottom: 6 }}><strong>Current status:</strong> {details.status} | <strong>Type:</strong> {details.event_type}</div>
            {details.status === 'scheduled' && (!details.scheduled_date || new Date(details.scheduled_date) > new Date()) && <div style={{ color: '#2563eb' }}>ℹ️ Event scheduled for future: {fmtDate(details.scheduled_date)}</div>}
            {details.status === 'active' && responseRate < 30 && missingReminders && <div style={{ color: '#dc2626' }}>⚠️ Low response rate ({responseRate}%) and no reminders configured — add reminder dates</div>}
            {details.status === 'active' && missingEmailTemplates && <div style={{ color: '#dc2626' }}>⚠️ Email templates missing — event cannot send invitations</div>}
            {details.status === 'active' && (details.total_invited ?? 0) === 0 && <div style={{ color: '#dc2626' }}>⚠️ No participants invited — event will not collect responses</div>}
            {details.status === 'active' && details.scheduled_date && new Date() > new Date(details.scheduled_date).getTime() + 7 * 24 * 60 * 60 * 1000 && <div style={{ color: '#d97706' }}>⚠️ Event active for &gt;7 days since scheduled date — consider marking as completed</div>}
            {responseRate > 0 && (details.total_responded ?? 0) === (details.total_invited ?? 0) && details.status !== 'completed' && <div style={{ color: '#16a34a' }}>✅ All participants responded — ready to mark as completed</div>}
          </div>
        </div>
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', color: '#0b5fff' }}>📖 View validation guide</summary>
          <div style={{ marginTop: 8, padding: 12, background: '#f9fafb', borderRadius: 6, fontSize: 12, fontFamily: 'monospace' }}>
            <div><strong>Expected Status Flow:</strong></div>
            <div>scheduled → active → completed (or cancelled)</div>
            <div style={{ marginTop: 8 }}><strong>Event Types:</strong></div>
            <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
              <li>check_in: Progress check with managers during action_planning or check_ins phase</li>
              <li>milestone: Celebrate action completion (100% progress)</li>
              <li>feedback_request: Collect feedback near campaign completion</li>
            </ul>
            <div style={{ marginTop: 8 }}><strong>Red Flags:</strong></div>
            <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
              <li>status=active but scheduled_date &gt;2 weeks ago → should be completed</li>
              <li>response_rate=0 after 3 days → check if invites sent</li>
              <li>total_invited=0 → event not initialized</li>
            </ul>
          </div>
        </details>
      </Section>

      <div>
        <Section title="Organization">
          <KeyValue label="Name" value={details.organization?.name || '-'} />
          <KeyValue label="ID" value={details.organization?.id || '-'} />
        </Section>

        <Section title="Campaign">
          {details.campaign ? (
            <div>
              <KeyValue label="Name" value={details.campaign.name} />
              <KeyValue label="ID" value={details.campaign.id} />
              <KeyValue label="Campaign org" value={details.campaign.organization?.name ? `${details.campaign.organization.name} (${details.campaign.organization.id})` : '-'} />
              <KeyValue label="Status / Phase" value={`${details.campaign.status || '-'} ${details.campaign.current_phase ? ` / ${details.campaign.current_phase}` : ''}`} />
              <KeyValue label="Created at" value={fmtDate(details.campaign.created_at)} />
              <KeyValue label="Completion rate" value={typeof details.campaign.completion_rate !== 'undefined' ? `${details.campaign.completion_rate}%` : '-'} />
              <KeyValue label="Avg action progress" value={typeof details.campaign.avg_action_progress !== 'undefined' ? `${details.campaign.avg_action_progress}%` : '-'} />
              <KeyValue label="Start" value={fmtDate(details.campaign.start_date)} />
              <KeyValue label="End" value={fmtDate(details.campaign.end_date)} />
              <KeyValue label="Created by" value={details.campaign.created_by_name || '-'} />
            </div>
          ) : <div style={{ color: '#777' }}>Not part of a campaign</div>}
        </Section>

        <Section title="Schedule & Reminders">
          <KeyValue label="Scheduled" value={fmtDate(details.scheduled_date)} />
          <KeyValue label="Launched" value={fmtDate(details.launch_date)} />
          <KeyValue label="Close" value={fmtDate(details.close_date)} />
          <KeyValue label="Reminders" value={Array.isArray(details.reminder_dates) && details.reminder_dates.length ? details.reminder_dates.join(', ') : '-'} />
         
        </Section>

        <Section title="Response & Participation">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
            <div style={{ textAlign: 'center', minWidth: 120 }}>
              <div style={{ color: '#666' }}>Invited</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{details.total_invited ?? '-'}</div>
            </div>
            <div style={{ textAlign: 'center', minWidth: 120 }}>
              <div style={{ color: '#666' }}>Responded</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{details.total_responded ?? '-'}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ color: '#666' }}>Response rate</div>
                <div style={{ fontSize: 12, color: '#333' }}>{String(responseRate).replace(/^\s+|\s+$/g, '')}%</div>
              </div>
              <div style={{ height: 12, background: '#eee', borderRadius: 6 }}>
                <div style={{ width: `${Math.min(100, Math.max(0, Number(responseRate) || 0))}%`, height: 12, background: '#0b5fff', borderRadius: 6 }} />
              </div>
            </div>
          </div>

          <div style={{ marginTop: 8 }}>
            <div style={{ color: '#333', fontWeight: 700, marginBottom: 6 }}>Response stats</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              <div style={{ padding: 8, background: '#fafafa', borderRadius: 6 }}><div style={{ color: '#666' }}>Total</div><div style={{ fontWeight: 700 }}>{details.response_stats?.total ?? '-'}</div></div>
              <div style={{ padding: 8, background: '#fafafa', borderRadius: 6 }}><div style={{ color: '#666' }}>Completed</div><div style={{ fontWeight: 700 }}>{details.response_stats?.completed ?? 0}</div></div>
              <div style={{ padding: 8, background: '#fafafa', borderRadius: 6 }}><div style={{ color: '#666' }}>In progress</div><div style={{ fontWeight: 700 }}>{details.response_stats?.in_progress ?? 0}</div></div>
              <div style={{ padding: 8, background: '#fafafa', borderRadius: 6 }}><div style={{ color: '#666' }}>Abandoned</div><div style={{ fontWeight: 700 }}>{details.response_stats?.abandoned ?? 0}</div></div>
            </div>
            <div style={{ marginTop: 8 }}><KeyValue label="Avg time (mins)" value={details.response_stats?.avg_time_minutes ?? '-'} /></div>
          </div>
        </Section>

        <Section title="Configuration & Rules">
          <KeyValue label="Include report data" value={details.event_config?.context_data?.include_report_data ? 'Yes' : 'No'} />
          <KeyValue label="Include action plans" value={details.event_config?.context_data?.include_action_plans ? 'Yes' : 'No'} />
          <KeyValue label="Include last check-in" value={details.event_config?.context_data?.include_last_check_in ? 'Yes' : 'No'} />
          <KeyValue label="Next steps required" value={details.event_config?.response_requirements?.next_steps_required ? 'Yes' : 'No'} />
          <KeyValue label="Minimum required actions" value={details.event_config?.response_requirements?.minimum_required_actions ?? '-'} />
          <KeyValue label="Progress update required" value={details.event_config?.response_requirements?.progress_update_required ? 'Yes' : 'No'} />
          <KeyValue label="Satisfaction rating required" value={details.event_config?.response_requirements?.satisfaction_rating_required ? 'Yes' : 'No'} />
          <div style={{ marginTop: 12 }}>
            <div style={{ color: '#333', fontWeight: 700, marginBottom: 6 }}>Custom email content (default)</div>
            <PrettyJSON obj={details.custom_email_content} />
          </div>
        </Section>

        <Section title="Email templates & custom data">
          <KeyValue label="Subject template" value={details.email_subject_template || '-'} />
          <div style={{ marginTop: 8 }}><div style={{ color: '#333', fontWeight: 700, marginBottom: 6 }}>Content template</div><pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12, borderRadius: 6 }}>{details.email_content_template || '-'}</pre></div>
          <div style={{ marginTop: 8 }}>
            <div style={{ color: '#333', fontWeight: 700, marginBottom: 6 }}>Custom email data</div>
            <PrettyJSON obj={details.custom_email_data} />
          </div>
         
        </Section>

        <Section title="Misc & Metadata">
          <KeyValue label="Raw created_at" value={details.created_at} />
          <KeyValue label="Raw updated_at" value={details.updated_at} />
          <KeyValue label="Event URL template" value={details.event_url_template || '-'} />
          <KeyValue label="Access code required" value={details.access_code_required ? 'Yes' : 'No'} />
          <KeyValue label="Created" value={fmtDate(details.created_at)} />
          <KeyValue label="Updated" value={fmtDate(details.updated_at)} />
        </Section>


        <Section title="Full API payload (raw)">
          <PrettyJSON obj={details} />
        </Section>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, alignItems: 'center' }}>
          <div>
            <Link to="/app/action/events">← Back to events</Link>
          </div>
          <div style={{ color: '#666', fontSize: 12 }}>
            <span style={{ marginRight: 12 }}>ID: {details.id}</span>
            <span>Type: {details.event_type || '-'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

